import React, { useEffect, useState } from 'react';
import { fetchFuelLogs, addFuelLog, updateFuelLog, deleteFuelLog, FuelLogEntry } from '../lib/fuelLogApi';
import { getCurrentUser } from '../lib/supabase';
import { formatMonthCard } from '../lib/dateUtils';
import FuelLogEntryRow from './FuelLogEntryRow';
import FuelLogForm from './FuelLogForm';
import { Plus } from 'lucide-react';
import { formatNumberSR, formatCurrencySR } from '../lib/formatUtils';

const FuelLogSection: React.FC = () => {
  const [entries, setEntries] = useState<FuelLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEntry, setEditEntry] = useState<FuelLogEntry | null>(null);

  // Group entries by month and sum price and liters
  const monthlyTotals = entries.reduce((acc, entry) => {
    const month = entry.date.slice(0, 7); // YYYY-MM
    if (!acc[month]) acc[month] = { total: 0, liters: 0 };
    acc[month].total += Number(entry.price);
    acc[month].liters += Number(entry.liter);
    return acc;
  }, {} as Record<string, { total: number; liters: number }>);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const data = await fetchFuelLogs();
      setEntries(data);
    } catch (e: any) {
      setError(e.message || 'Failed to fetch fuel logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleAdd = async (data: { liter: number; price: number; date: string }) => {
    setError(null);
    setLoading(true);
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('Not logged in');
      await addFuelLog({
        user_id: user.id,
        ...data,
      });
      setShowAddModal(false);
      fetchEntries();
    } catch (e: any) {
      setError(e.message || 'Failed to add entry');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry: FuelLogEntry) => {
    setEditEntry(entry);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (data: { liter: number; price: number; date: string }) => {
    if (!editEntry) return;
    setLoading(true);
    setError(null);
    try {
      await updateFuelLog(editEntry.id, data);
      setShowEditModal(false);
      setEditEntry(null);
      fetchEntries();
    } catch (e: any) {
      setError(e.message || 'Failed to update entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await deleteFuelLog(id);
      fetchEntries();
    } catch (e: any) {
      setError(e.message || 'Failed to delete entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-theme-primary">Fuel Log</h1>
          <button
            className="bg-primary-600 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-primary-700 transition-colors"
            onClick={() => setShowAddModal(true)}
            title="Add Fuel Entry"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
        {error && <div className="text-red-600 mb-4">{error}</div>}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-theme-primary mb-2">Monthly Fuel Spending</h2>
          <div className="flex gap-4 overflow-x-auto pb-2 hide-scrollbar">
            {Object.entries(monthlyTotals).map(([month, data]) => {
              const avg = data.liters > 0 ? data.total / data.liters : 0;
              return (
                <div key={month} className="bg-theme-primary rounded-lg shadow-sm border border-theme-primary p-4 flex flex-col min-w-[180px] relative">
                  <div className="absolute top-2 left-3 text-xs text-theme-tertiary font-semibold">
                    {formatMonthCard(month)}
                  </div>
                  <div className="flex-1 flex flex-col justify-center items-center pt-6">
                    <div className="text-base font-semibold text-theme-primary mb-1">{formatCurrencySR(data.total)}</div>
                    <div className="text-sm text-theme-secondary font-medium whitespace-nowrap">{formatNumberSR(data.liters, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L</div>
                    <div className="text-xs text-theme-tertiary font-medium flex items-center gap-1 mt-1">
                      <span title="Average RSD per Liter">∅</span>
                      <span>{avg > 0 ? formatCurrencySR(avg) : '--'}/L</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {Object.keys(monthlyTotals).length === 0 && (
              <div className="text-theme-tertiary text-center">No fuel spending yet.</div>
            )}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-theme-primary mb-2">Fuel Log Entries</h2>
          {loading ? (
            <div className="text-theme-secondary text-center">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="text-theme-tertiary text-center">No fuel log entries found.</div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {entries.map(entry => (
                <FuelLogEntryRow
                  key={entry.id}
                  entry={entry}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
        {/* Add Modal */}
        <FuelLogForm
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAdd}
        />
        {/* Edit Modal */}
        <FuelLogForm
          open={showEditModal}
          onClose={() => { setShowEditModal(false); setEditEntry(null); }}
          onSubmit={handleEditSubmit}
          initialData={editEntry || undefined}
        />
      </div>
    </div>
  );
};

export default FuelLogSection; 